<template>
    <div class="col-12">
        <div class="card card-outline card-info shadow-lg border-0 rounded-lg">
            <div class="card-header bg-white border-bottom-0 pt-4 pb-2 px-4">
                <div class="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 class="card-title font-weight-bold text-dark mb-1" style="font-size: 1.5rem;">
                            <i class="fas fa-cloud-upload-alt text-info mr-2"></i> Carga Masiva de Recibos
                        </h3>
                        <p class="text-muted small mb-0">Sube tus archivos ZIP o PDF de forma segura y rápida.</p>
                    </div>
                    <button type="button" class="btn btn-light btn-sm rounded-pill px-3 shadow-sm" @click="$router.go(-1)">
                        <i class="fas fa-arrow-left mr-1"></i> Regresar
                    </button>
                </div>
            </div>
            
            <div class="card-body px-4 pb-5">
                <!-- Dropzone Area -->
                <div 
                    v-if="!uploading && !uploadComplete"
                    class="dropzone-container mb-4 d-flex flex-column justify-content-center align-items-center"
                    :class="{ 'drag-active': isDragActive }"
                    @dragover.prevent="isDragActive = true"
                    @dragleave.prevent="isDragActive = false"
                    @drop.prevent="handleDrop"
                    @click="triggerFileInput"
                >
                    <input type="file" ref="fileInput" multiple @change="handleFiles" style="display: none;" accept=".zip,.pdf">
                    <div class="icon-circle mb-3">
                        <i class="fas fa-cloud-upload-alt fa-3x text-info"></i>
                    </div>
                    <h5 class="font-weight-bold text-dark mb-2">Arrastra y suelta tus archivos aquí</h5>
                    <p class="text-muted mb-3">o haz clic para explorar tu ordenador</p>
                    <div class="badge badge-light px-3 py-2 text-muted border">
                        <i class="fas fa-info-circle mr-1"></i> Formato: RE_8100_Quincenal_YYYY_00_000_ECE
                    </div>
                </div>

                <!-- File List & Progress -->
                <div v-if="files.length > 0">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="font-weight-bold text-dark m-0">
                            Archivos Seleccionados <span class="badge badge-info badge-pill ml-2">{{ files.length }}</span>
                        </h5>
                        <button v-if="!uploading && !uploadComplete" class="btn btn-success btn-lg shadow-sm px-5 rounded-pill" @click="startUpload">
                            <i class="fas fa-rocket mr-2"></i> Iniciar Carga
                        </button>
                    </div>

                    <div class="file-list-container">
                        <transition-group name="list" tag="div">
                            <div v-for="(item, index) in files" :key="item.file.name + index" class="file-card mb-3 p-3 rounded shadow-sm border-left-custom" :class="getStatusClass(item.status)">
                                <div class="d-flex align-items-center justify-content-between mb-2">
                                    <div class="d-flex align-items-center overflow-hidden">
                                        <div class="file-icon mr-3">
                                            <i :class="getFileIcon(item.file.name)"></i>
                                        </div>
                                        <div class="file-info text-truncate">
                                            <h6 class="font-weight-bold mb-0 text-truncate" :title="item.file.name">{{ item.file.name }}</h6>
                                            <small class="text-muted">{{ formatSize(item.file.size) }}</small>
                                        </div>
                                    </div>
                                    <div class="file-status text-right" style="min-width: 120px;">
                                        <span v-if="item.status === 'pending'" class="badge badge-light border">Pendiente</span>
                                        <span v-if="item.status === 'uploading'" class="badge badge-info">Subiendo...</span>
                                        <span v-if="item.status === 'success'" class="badge badge-success"><i class="fas fa-check mr-1"></i> Completado</span>
                                        <span v-if="item.status === 'error'" class="badge badge-danger"><i class="fas fa-times mr-1"></i> Error</span>
                                        
                                        <button v-if="item.status === 'pending' && !uploading" class="btn btn-link text-danger btn-sm p-0 ml-2" @click.stop="removeFile(index)">
                                            <i class="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                <!-- Progress Bar -->
                                <div class="progress" style="height: 6px; border-radius: 3px;">
                                    <div 
                                        class="progress-bar" 
                                        :class="getProgressBarClass(item.status)"
                                        role="progressbar" 
                                        :style="{ width: item.progress + '%' }" 
                                        :aria-valuenow="item.progress" 
                                        aria-valuemin="0" 
                                        aria-valuemax="100"
                                    ></div>
                                </div>
                                <div v-if="item.message" class="mt-2 small" :class="item.status === 'error' ? 'text-danger' : 'text-success'">
                                    {{ item.message }}
                                </div>
                            </div>
                        </transition-group>
                    </div>
                </div>

                <!-- Summary -->
                <div v-if="uploadComplete" class="mt-5 text-center">
                    <div class="row justify-content-center">
                        <div class="col-md-8">
                            <div class="card border-0 shadow-sm bg-light">
                                <div class="card-body">
                                    <h4 class="font-weight-bold mb-4">Resumen de Carga</h4>
                                    <div class="row text-center">
                                        <div class="col-6 border-right">
                                            <h2 class="text-success font-weight-bold display-4">{{ successCount }}</h2>
                                            <p class="text-muted text-uppercase font-weight-bold small">Exitosos</p>
                                        </div>
                                        <div class="col-6">
                                            <h2 class="text-danger font-weight-bold display-4">{{ errorCount }}</h2>
                                            <p class="text-muted text-uppercase font-weight-bold small">Fallidos</p>
                                        </div>
                                    </div>
                                    <button class="btn btn-primary btn-lg mt-4 px-5 rounded-pill shadow" @click="reset">
                                        <i class="fas fa-redo mr-2"></i> Cargar más archivos
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import request from '../../services/request';

export default {
    name: "upload-receipts",
    data() {
        return {
            files: [], // Array of objects: { file, status, progress, message }
            uploading: false,
            uploadComplete: false,
            isDragActive: false
        };
    },
    computed: {
        successCount() {
            return this.files.filter(f => f.status === 'success').length;
        },
        errorCount() {
            return this.files.filter(f => f.status === 'error').length;
        }
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFiles(event) {
            this.addFiles(event.target.files);
        },
        handleDrop(event) {
            this.isDragActive = false;
            this.addFiles(event.dataTransfer.files);
        },
        addFiles(fileList) {
            for (let i = 0; i < fileList.length; i++) {
                this.files.push({
                    file: fileList[i],
                    status: 'pending',
                    progress: 0,
                    message: ''
                });
            }
        },
        removeFile(index) {
            this.files.splice(index, 1);
        },
        formatSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        getFileIcon(filename) {
            const ext = filename.split('.').pop().toLowerCase();
            if (ext === 'pdf') return 'fas fa-file-pdf text-danger fa-lg';
            if (ext === 'zip') return 'fas fa-file-archive text-warning fa-lg';
            return 'fas fa-file text-secondary fa-lg';
        },
        getStatusClass(status) {
            if (status === 'success') return 'border-left-success bg-white';
            if (status === 'error') return 'border-left-danger bg-white';
            if (status === 'uploading') return 'border-left-info bg-white';
            return 'border-left-secondary bg-white';
        },
        getProgressBarClass(status) {
            if (status === 'success') return 'bg-success';
            if (status === 'error') return 'bg-danger';
            return 'bg-info progress-bar-striped progress-bar-animated';
        },
        async startUpload() {
            this.uploading = true;
            this.uploadComplete = false;

            // Process files sequentially to avoid overwhelming the server, 
            // but UI updates individually.
            // Could be parallelized with Promise.allLimit if needed.
            for (let i = 0; i < this.files.length; i++) {
                let item = this.files[i];
                if (item.status === 'success') continue; // Skip already uploaded

                item.status = 'uploading';
                item.progress = 10; // Start progress

                let formData = new FormData();
                formData.append('file', item.file);

                try {
                    // Simulate progress for better UX since local uploads are instant
                    let progressInterval = setInterval(() => {
                        if (item.progress < 90) item.progress += 10;
                    }, 100);

                    await request.post('/payroll-receipts', formData);
                    
                    clearInterval(progressInterval);
                    item.progress = 100;
                    item.status = 'success';
                    item.message = 'Archivo cargado correctamente.';
                } catch (error) {
                    item.progress = 100;
                    item.status = 'error';
                    let msg = 'Error desconocido';
                    if (error.response && error.response.data && error.response.data.message) {
                        msg = error.response.data.message;
                    }
                    item.message = msg;
                }
            }

            this.uploading = false;
            this.uploadComplete = true;
        },
        reset() {
            this.files = [];
            this.uploading = false;
            this.uploadComplete = false;
        }
    }
};
</script>

<style scoped>
.dropzone-container {
    border: 2px dashed #cbd5e0;
    border-radius: 1rem;
    padding: 3rem;
    cursor: pointer;
    background-color: #f8fafc;
    transition: all 0.3s ease;
}

.dropzone-container:hover, .dropzone-container.drag-active {
    background-color: #ebf8ff;
    border-color: #4299e1;
    transform: scale(1.01);
}

.icon-circle {
    width: 80px;
    height: 80px;
    background-color: #e6fffa;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
}

.icon-circle i {
    color: #38b2ac;
}

.file-card {
    transition: all 0.3s ease;
    border: 1px solid #e2e8f0;
}

.file-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.border-left-custom {
    border-left: 4px solid transparent;
}

.border-left-success { border-left-color: #48bb78; }
.border-left-danger { border-left-color: #f56565; }
.border-left-info { border-left-color: #4299e1; }
.border-left-secondary { border-left-color: #cbd5e0; }

/* List Transitions */
.list-enter-active, .list-leave-active {
    transition: all 0.5s;
}
.list-enter, .list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}
</style>
